import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Graph } from 'libs/cotera-sdk/src/core/dg/graph';
import { queryKeys } from './query-cache-keys';
import { useTenantedClient, useWhoami } from '../stores/org';
import { Assert } from '@cotera/utilities';
import { ToolListItem as ToolListItemResponse } from '../etc/api/typed-client';
import { AppModel } from '../v1/shell/app.model';
import {
  useStatefullyAwareInScopeModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { snakeCase } from 'lodash';

// Stable fallback watchables used when no AppModel is in scope — a subtree
// mounted outside the app shell, or a test harness. They are never read for
// their value in that case — the fallback react-query below is the source of
// truth — but useWatchableValue requires a non-null watchable.
const EMPTY_TOOLS_WATCHABLE = Watchable.fromValue<ToolListItemResponse[]>([]);
const READY_STATUS_WATCHABLE = Watchable.fromValue<'loading' | 'ready'>(
  'ready'
);

const FIVE_SECONDS = 5000;

// Tool lookups run once per ToolPartComponent (100s of tool parts per chat) and
// re-normalize keys with lodash `snakeCase` against the whole org tool list
// (130-180+ entries). Build the lookup index once per tools payload and share
// it across all consumers via a module-level WeakMap keyed on the react-query
// array identity. React Query's structural sharing preserves that identity
// across the 5s refetch, so the entry stays hot; when the array is replaced the
// old entry is GC'd with it.

// `snakeCase(key) -> tool`, first-wins on duplicate snake keys (mirrors the
// `tools.find((t) => snakeCase(t.key) === snakeCase(toolKey))` it replaces).
const toolIndexBySnakeKeyCache = new WeakMap<
  ToolListItemResponse[],
  Map<string, ToolListItemResponse>
>();

export const toolIndexBySnakeKey = (
  tools: ToolListItemResponse[]
): Map<string, ToolListItemResponse> => {
  const cached = toolIndexBySnakeKeyCache.get(tools);
  if (cached !== undefined) {
    return cached;
  }
  const index = new Map<string, ToolListItemResponse>();
  for (const tool of tools) {
    const key = snakeCase(tool.key);
    if (!index.has(key)) {
      index.set(key, tool); // first-wins
    }
  }
  toolIndexBySnakeKeyCache.set(tools, index);
  return index;
};

// Index over BOTH snake and raw keys. Distinct from the snake-only index above:
// raw keys are inserted last, so a raw key wins on collision with a snake key —
// matching the original `Object.fromEntries([...snake, ...raw])` (later wins).
const toolIndexByAnyKeyCache = new WeakMap<
  ToolListItemResponse[],
  Map<string, ToolListItemResponse>
>();

export const toolIndexByAnyKey = (
  tools: ToolListItemResponse[]
): Map<string, ToolListItemResponse> => {
  const cached = toolIndexByAnyKeyCache.get(tools);
  if (cached !== undefined) {
    return cached;
  }
  const index = new Map<string, ToolListItemResponse>();
  for (const tool of tools) {
    index.set(snakeCase(tool.key), tool);
  }
  for (const tool of tools) {
    index.set(tool.key, tool); // raw keys win on collision
  }
  toolIndexByAnyKeyCache.set(tools, index);
  return index;
};
/**
 * Keeps the tools cache warm; the data is read elsewhere, never here.
 *
 * Mounted at the top of the org app, so re-rendering on each poll would re-render
 * the entire application every five seconds. `notifyOnChangeProps: []` opts this
 * subscriber out of notifications entirely — the query still runs on its
 * interval and still updates the cache, so consumers refresh as before, but the
 * component that owns the polling no longer wakes up for it.
 */
export const useToolSyncEffect = () => {
  const orgId = useWhoami((x) => x.org.id);
  const client = useTenantedClient();

  useQuery({
    queryKey: queryKeys.toolsV2.all({ orgId }),
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.list());
    },
    refetchInterval: FIVE_SECONDS,
    notifyOnChangeProps: [],
  });
};

/**
 * Non-suspense version of useTools. Prefers the shared tools watchable held on
 * AppModel, which is fetched once and refreshed on tool-credential-updated
 * events — so consumers update without re-querying. Returns an empty array
 * while the first load is in flight.
 *
 * When no AppModel is in scope — a subtree mounted outside the app shell, or a
 * test harness — it falls back to a direct react-query so consumers like
 * useCocoMentions still resolve tools instead of crashing. That query shares the
 * global queryClient cache, so the shell's tool-credential-updated query-sync
 * watchable still keeps it fresh.
 */
export const useToolsAsync = (): {
  tools: ToolListItemResponse[];
  isLoading: boolean;
} => {
  const appModel = useStatefullyAwareInScopeModel(AppModel);
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();

  // Only fetches when AppModel is absent; otherwise disabled and the watchables
  // below drive the value.
  const fallback = useQuery({
    queryKey: queryKeys.toolsV2.all({ orgId }),
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.list());
    },
    enabled: appModel === null,
  });

  const watchableTools = useWatchableValue(
    appModel?.tools ?? EMPTY_TOOLS_WATCHABLE
  );
  const watchableStatus = useWatchableValue(
    appModel?.toolsStatus ?? READY_STATUS_WATCHABLE
  );

  const tools = appModel !== null ? watchableTools : fallback.data ?? [];
  const isLoading =
    appModel !== null ? watchableStatus === 'loading' : fallback.isLoading;

  return {
    tools: tools.filter((tool) => tool.implementationType !== 'legacy'),
    isLoading,
  };
};

/**
 * important - ONLY returns new style tools - not legacy tools
 * Suspends until tools are loaded.
 * @returns
 */
export const useTools = () => {
  const orgId = useWhoami((s) => s.org.id);
  const queryKey = queryKeys.toolsV2.all({ orgId });
  const client = useTenantedClient();
  const { data: tools } = useSuspenseQuery({
    queryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.list());
    },
  });

  return tools.filter((tool) => tool.implementationType !== 'legacy');
};

export const useToolResolver = (): Graph.IToolResolver => {
  const client = useQueryClient();
  const orgId = useWhoami((x) => x.org.id);

  const getToolsByKey = (): Map<string, ToolListItemResponse> => {
    const toolList =
      client.getQueryData<ToolListItemResponse[]>(
        queryKeys.toolsV2.all({ orgId })
      ) ?? [];
    return toolIndexByAnyKey(toolList);
  };

  return {
    isToolAvailable: (toolKey: string): boolean => {
      // Existence only: an unconnected OAuth tool still resolves (warning
      // shown separately via ToolConfigurationIndicator), not a graph error.
      const toolsByKey = getToolsByKey();
      return toolsByKey.get(snakeCase(toolKey)) !== undefined;
    },
    getToolInfo: (toolKey: string): Graph.ToolInfo | null => {
      const toolsByKey = getToolsByKey();
      const tool = toolsByKey.get(snakeCase(toolKey));
      if (!tool) {
        return null;
      }
      return {
        key: tool.key,
        inputTy: tool.inputTy as any,
        outputTy: tool.outputTy as any,
        name: tool.key,
      };
    },
    list: (): string[] => {
      const toolsByKey = getToolsByKey();
      return [...toolsByKey.keys()];
    },
  };
};

/**
 * Suspenseful hook to get tool info by key.
 * Suspends until tools data is available, then returns the matching tool.
 */
export const useSuspensfulToolInfo = (
  toolKey: string
): ToolListItemResponse | null => {
  const orgId = useWhoami((s) => s.org.id);
  const queryKey = queryKeys.toolsV2.all({ orgId });
  const client = useTenantedClient();

  const { data: tools } = useSuspenseQuery({
    queryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.list());
    },
  });

  return toolIndexBySnakeKey(tools).get(snakeCase(toolKey)) ?? null;
};
